import { useEffect, useRef } from "react";

import styles from "./undo-toast.module.css";

/** How long the message stays: long enough to notice a mistake and reach Undo. */
const SHOWN_MS = 8000;

/**
 * A message at the foot of the window saying what just changed, with **Undo**
 * beside it (BT10, 2026-10-03).
 *
 * The first is for a baby tooth replaced on the chart, where a double-click is
 * all it takes and a slip is easy: the client asked for *"the option to revert
 * an action in case I accidentally select a tooth to swap by mistake"*. A
 * confirm before every swap was the other way to catch it, and would have put a
 * question in front of every right swap to catch the occasional wrong one.
 *
 * It goes by itself after a few seconds, so the caller renders it with a `key`
 * per message: a new message is a new toast with its own full time. Without
 * `onUndo` it is only the message, as after an undo or a failure.
 */
export function UndoToast({
  message,
  onUndo,
  onDismiss,
  testId,
}: {
  message: string;
  onUndo?: () => void;
  onDismiss: () => void;
  testId?: string;
}): React.JSX.Element {
  // Read when the time runs out, so a parent's fresh arrow does not restart it.
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  });

  useEffect(() => {
    const timer = window.setTimeout(
      () => dismiss.current(),
      onUndo === undefined ? 3000 : SHOWN_MS,
    );
    return () => window.clearTimeout(timer);
  }, [onUndo]);

  return (
    <div className={styles.toast} role="status" data-testid={testId}>
      <span className={styles.message}>{message}</span>
      {onUndo !== undefined && (
        <button
          type="button"
          className={styles.undo}
          onClick={onUndo}
          data-testid={testId === undefined ? undefined : `${testId}-undo`}
        >
          Undo
        </button>
      )}
    </div>
  );
}
